import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateSwitchSx,
  harmonateAccentLinkSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';



// Toast implementation with queue
const useToast = () => {
  const [toastQueue, setToastQueue] = useState([]);

  const showSuccess = (message, title = 'Success') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'success', title }]);
  };

  const showError = (message, title = 'Error') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'error', title }]);
  };

  const showInfo = (message, title = 'Info') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'info', title }]);
  };

  const hideToast = () => {
    setToastQueue(prev => prev.slice(1));
  };

  useEffect(() => {
    if (toastQueue.length > 0) {
      const timer = setTimeout(() => {
        hideToast();
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [toastQueue]);

  return { toastState: toastQueue[0] || { open: false, message: '', severity: 'success', title: '' }, showSuccess, showError, showInfo, hideToast };
};

const stepTypeOptions = [
  'EXTRACT',
  'TRANSFORM',
  'NOTIFY',
  'COMPARE'
];

const errorHandleTypeOptions = [
  'retry_then_notify',
  'continue_on_error',
  'stop_on_error',
  'skip_and_log',
  'manual_review_required'
];

const columns = [
  { field: 'stepOrder', headerName: 'Order', flex: 0.5, minWidth: 80 },
  { field: 'stepName', headerName: 'Step Name', flex: 1.5, minWidth: 150 },
  { field: 'stepType', headerName: 'Type', flex: 1, minWidth: 100 },
  // { field: 'stepSubType', headerName: 'Sub Type', flex: 1, minWidth: 120 },
  // { field: 'errorHandleType', headerName: 'Error Handle Type', flex: 1.2, minWidth: 150 },
  { field: 'userEditable', headerName: 'User Editable', flex: 0.8, minWidth: 100 },
  { field: 'visibleToCustomer', headerName: 'Visible to Customer', flex: 1, minWidth: 130 },
  { field: 'snowflakeViews', headerName: 'Snowflake Views', flex: 1.3, minWidth: 150 },
  { field: 'snowflakeDestinationTable', headerName: 'Snowflake Dest Table', flex: 1.3, minWidth: 160 },
  // { field: 'gridViewName', headerName: 'Grid View Name', flex: 1.2, minWidth: 140 },
  { field: 'runCondition', headerName: 'Run Condition', flex: 1, minWidth: 120 },
  { field: 'onExit', headerName: 'On Exit', flex: 0.8, minWidth: 100 },
  { field: 'onValidation', headerName: 'On Validation', flex: 1, minWidth: 120 },
  { field: 'validationErrorStep', headerName: 'Validation Error Step', flex: 1.2, minWidth: 170 },
  { field: 'onError', headerName: 'On Error', flex: 0.8, minWidth: 100 },
  { field: 'createCheckerScreenTab', headerName: 'Checker Screen Tab', flex: 1.2, minWidth: 150 },
  { field: 'transformDescription', headerName: 'Transform Description', flex: 1.5, minWidth: 180 },
  { field: 'userActionsTaken', headerName: 'User Actions Taken', flex: 1.3, minWidth: 160 },
  { field: 'additionalInstructions', headerName: 'Additional Instructions', flex: 1.5, minWidth: 180 },
  // { field: 'updatedBy', headerName: 'Updated By', flex: 1, minWidth: 120 },
  { field: 'isDeleted', headerName: 'Is Deleted', flex: 0.8, minWidth: 100 },
];

// const TenantWorkflowStepsDrawer = ({ open, onClose, workflowId }) => {
const TenantWorkflowStepsDrawer = ({ open, onClose, workflowDefinitionId }) => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.TENANT_WORKFLOW);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [jsonTemplateOpen, setJsonTemplateOpen] = useState(false);

  const [formData, setFormData] = useState({
    workflowDefinitionId: '',
    stepName: '',
    stepOrder: '',
    stepType: '',
    stepSubType: '',
    errorHandleType: '',
    userEditable: false,
    snowflakeViews: '',
    gridViewName: '',
    visibleToCustomer: false,
    snowflakeDestinationTable: '',
    runCondition: '',
    onExit: '',
    onValidation: '',
    validationErrorStep: '',
    onError: '',
    createCheckerScreenTab: '',
    transformDescription: '',
    userActionsTaken: '',
    additionalInstructions: '',
    updatedBy: '',
    isDeleted: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { toastState, showSuccess, showError, hideToast } = useToast();

  const [viewConfigOpen, setViewConfigOpen] = useState(false);
  // Add these states after the existing useState declarations
  const [configData, setConfigData] = useState(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [creatingConfig, setCreatingConfig] = useState(false);
  const [editedJson, setEditedJson] = useState('');
  const [newConfig, setNewConfig] = useState({
    viewDCategory: '',
    viewJsonDefault: ''
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [compareInfo, setCompareInfo] = useState(null);

  const fetchConfig = async () => {
    if (!formData.additionalInstructions) {
      setConfigData(null);
      setConfigError('No additionalInstructions provided');
      return;
    }

    try {
      setConfigLoading(true);
      setConfigError(null);
      const parsed = JSON.parse(formData.additionalInstructions);
      const viewName = parsed.grid?.view_name;
      if (!viewName) {
        throw new Error('No view_name found in additionalInstructions.grid');
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${viewName}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) throw new Error(`Failed to fetch config: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setConfigData(data);
      setEditedJson(JSON.stringify(data, null, 2));

      // Extract compare information if this is a COMPARE step
      if (data.compare) {
        setCompareInfo({
          sourceView: data.compare.source_view,
          compareView: data.compare.compare_view
        });
      } else {
        setCompareInfo(null);
      }
    } catch (err) {
      setConfigError(err.message);
      setConfigData(null);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.additionalInstructions) {
      setConfigError('No additionalInstructions provided');
      return;
    }

    try {
      setConfigSaving(true);
      setConfigError(null);

      const parsed = JSON.parse(formData.additionalInstructions);
      const viewName = parsed.grid?.view_name;
      if (!viewName) {
        throw new Error('No view_name found in additionalInstructions.grid');
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Update existing config
      const updateResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${viewName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(JSON.parse(editedJson)),
      });

      if (!updateResp.ok) {
        throw new Error(`Failed to update config: ${updateResp.status} ${updateResp.statusText}`);
      }

      // Refresh the config
      await fetchConfig();
      setEditingConfig(false);
      showSuccess('Configuration updated successfully!');
    } catch (err) {
      setConfigError(err.message);
      showError('Failed to update configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleCreateConfig = async () => {
    try {
      setConfigSaving(true);
      setConfigError(null);

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Create new config
      const createResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          viewDCategory: newConfig.viewDCategory,
          viewJsonDefault: newConfig.viewJsonDefault
        }),
      });

      if (!createResp.ok) {
        throw new Error(`Failed to create config: ${createResp.status} ${createResp.statusText}`);
      }

      // Update the form data with the new view name
      const updatedInstructions = {
        ...(formData.additionalInstructions ? JSON.parse(formData.additionalInstructions) : {}),
        grid: {
          ...(formData.additionalInstructions && JSON.parse(formData.additionalInstructions).grid || {}),
          view_name: newConfig.viewDCategory
        }
      };

      setFormData({
        ...formData,
        additionalInstructions: JSON.stringify(updatedInstructions, null, 2)
      });

      // Close the create dialog and refresh config
      setCreatingConfig(false);
      await fetchConfig();
      showSuccess('Configuration created successfully!');
    } catch (err) {
      setConfigError(err.message);
      showError('Failed to create configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const fetchWorkflowSteps = async () => {
    if (!workflowDefinitionId) return;

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) throw new Error('No tenant ID found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/tenant/${tenantId}?tenantWorkflowId=${workflowDefinitionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) throw new Error(`Failed to fetch workflow steps: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      // Sort steps by stepOrder before setting them in state
      const sortedSteps = Array.isArray(data)
        ? [...data].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0))
        : [];
      setSteps(sortedSteps);
    } catch (err) {
      setError(err.message);
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };
  // const fetchStepDetails = async (workflowStepId) => {
  //   try {
  //     setFormLoading(true);
  //     const token = localStorage.getItem('token');
  //     if (!token) throw new Error('No authentication token found');

  //     const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/${workflowStepId}`, {
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     if (!resp.ok) throw new Error(`Failed to fetch step details: ${resp.status} ${resp.statusText}`);
  //     const data = await resp.json();
  //     setSelectedStep(data);

  //     // Reset compareInfo when loading a new step
  //     setCompareInfo(null);

  //     setFormData({
  //       workflowDefinitionId: data.workflowDefinitionId || '',
  //       stepName: data.stepName || '',
  //       stepOrder: data.stepOrder || '',
  //       stepType: data.stepType || '',
  //       stepSubType: data.stepSubType || '',
  //       errorHandleType: data.errorHandleType || '',
  //       userEditable: data.userEditable || false,
  //       snowflakeViews: data.snowflakeViews || '',
  //       gridViewName: data.gridViewName || '',
  //       visibleToCustomer: data.visibleToCustomer || false,
  //       snowflakeDestinationTable: data.snowflakeDestinationTable || '',
  //       runCondition: data.runCondition || '',
  //       onExit: data.onExit || '',
  //       onValidation: data.onValidation || '',
  //       onError: data.onError || '',
  //       createCheckerScreenTab: data.createCheckerScreenTab || '',
  //       transformDescription: data.transformDescription || '',
  //       userActionsTaken: data.userActionsTaken || '',
  //       additionalInstructions: data.additionalInstructions || '',
  //       updatedBy: data.updatedBy || '',
  //       isDeleted: data.isDeleted || false
  //     });

  //     setFormOpen(true);
  //   } catch (err) {
  //     showError(`Failed to fetch step details: ${err.message}`, 'Fetch Error');
  //   } finally {
  //     setFormLoading(false);
  //   }
  // };

  const fetchStepDetails = async (workflowStepId) => {
    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/${workflowStepId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) throw new Error(`Failed to fetch step details: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setSelectedStep(data);
      setCompareInfo(null);
      // Reset cached config so View Config / compare UI cannot show a previous step's config
      setConfigData(null);
      setEditedJson('');
      setConfigError(null);
      setEditingConfig(false);

      const additionalInstructions = data.additionalInstructions || '';

      setFormData({
        workflowDefinitionId: data.workflowDefinitionId || '',
        stepName: data.stepName || '',
        stepOrder: data.stepOrder || '',
        stepType: data.stepType || '',
        stepSubType: data.stepSubType || '',
        errorHandleType: data.errorHandleType || '',
        userEditable: data.userEditable || false,
        snowflakeViews: data.snowflakeViews || '',
        gridViewName: data.gridViewName || '',
        visibleToCustomer: data.visibleToCustomer || false,
        snowflakeDestinationTable: data.snowflakeDestinationTable || '',
        runCondition: data.runCondition || '',
        onExit: data.onExit || '',
        onValidation: data.onValidation || '',
        validationErrorStep: data.validationErrorStep || '',
        onError: data.onError || '',
        createCheckerScreenTab: data.createCheckerScreenTab || '',
        transformDescription: data.transformDescription || '',
        userActionsTaken: data.userActionsTaken || '',
        additionalInstructions,
        updatedBy: data.updatedBy || '',
        isDeleted: data.isDeleted || false
      });

      // Auto-fetch config for COMPARE steps
      if (data.stepType === 'COMPARE' && additionalInstructions) {
        try {
          const parsed = JSON.parse(additionalInstructions);
          const viewName = parsed.grid?.view_name;
          if (viewName) {
            const configResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${viewName}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (configResp.ok) {
              const configJson = await configResp.json();
              setConfigData(configJson);
              setEditedJson(JSON.stringify(configJson, null, 2));
              if (configJson.compare) {
                setCompareInfo({
                  sourceView: configJson.compare.source_view,
                  compareView: configJson.compare.compare_view
                });
              }
            }
          }
        } catch (err) {
          console.warn('Could not auto-fetch compare config:', err.message);
        }
      }

      setFormOpen(true);
    } catch (err) {
      showError(`Failed to fetch step details: ${err.message}`, 'Fetch Error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const isValidJSON = (str) => {
    if (!str) return true;
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const formatJsonString = (jsonString) => {
    try {
      // Try to parse the JSON
      const parsed = JSON.parse(jsonString);
      // If successful, return properly formatted JSON string
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If not valid JSON, return the original string
      return jsonString;
    }
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!isValidJSON(formData.additionalInstructions)) {
      showError('Additional Instructions must be valid JSON or empty', 'Validation Error');
      return;
    }

    try {
      setFormLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const payload = {
        // workflowDefinitionId: parseInt(formData.workflowDefinitionId),
        // tenantWorkflowId: workflowId, // Add this - use the workflowId prop
        workflowDefinitionId: workflowDefinitionId, // Use the prop instead of tenantWorkflowId
        stepOrder: parseInt(formData.stepOrder),
        stepName: formData.stepName,
        stepType: formData.stepType,
        visibleToCustomer: formData.visibleToCustomer,
        userEditable: formData.userEditable,
        isDeleted: formData.isDeleted,
      };

      // Add workflowStepId for updates
      if (!isCreating && selectedStep && selectedStep.workflowStepId) {
        payload.workflowStepId = selectedStep.workflowStepId;
      }

      // In the handleFormSubmit function, before the API call, add:
      if (isCreating) {
        payload.tenantId = localStorage.getItem('tenant_id');
        payload.tenantWorkflowId = workflowDefinitionId;
      } else {
        // For updates, preserve the existing tenantId and tenantWorkflowId
        payload.tenantId = formData.tenantId || localStorage.getItem('tenant_id');
        payload.tenantWorkflowId = formData.tenantWorkflowId || workflowDefinitionId;
      }

      // Handle JSON string fields - ensure they're valid JSON strings
      if (formData.stepSubType) {
        try {
          // If it's already JSON, keep it; if not, wrap it
          JSON.parse(formData.stepSubType);
          payload.stepSubType = formData.stepSubType;
        } catch {
          payload.stepSubType = JSON.stringify({ type: formData.stepSubType });
        }
      }

      payload.additionalInstructions = formData.additionalInstructions || '';

      // Preserve workflow step payload set in DB (not editable in UI)
      if (!isCreating && selectedStep?.payload) {
        payload.payload = selectedStep.payload;
      }

      if (formData.userActionsTaken) {
        try {
          // If it's already JSON, keep it; if not, wrap it
          JSON.parse(formData.userActionsTaken);
          payload.userActionsTaken = formData.userActionsTaken;
        } catch {
          payload.userActionsTaken = JSON.stringify({ actions: formData.userActionsTaken });
        }
      } else {
        payload.userActionsTaken = '';
      }

      if (formData.snowflakeViews) {
        try {
          // If it's already JSON, keep it; if not, wrap it
          JSON.parse(formData.snowflakeViews);
          payload.snowflakeViews = formData.snowflakeViews;
        } catch {
          payload.snowflakeViews = JSON.stringify({ views: formData.snowflakeViews });
        }
      } else {
        payload.snowflakeViews = '';
      }

      // Always include optional strings (including empty) so clearing a field persists.
      // The PUT mapper ignores null/omitted properties, so a missing key leaves the old DB value.
      payload.transformDescription = formData.transformDescription || '';
      payload.snowflakeDestinationTable = formData.snowflakeDestinationTable || '';
      payload.runCondition = formData.runCondition || '';
      payload.onExit = formData.onExit || '';
      payload.onValidation = formData.onValidation || '';
      payload.validationErrorStep = formData.validationErrorStep || '';
      payload.onError = formData.onError || '';
      payload.createCheckerScreenTab = formData.createCheckerScreenTab || '';
      if (formData.errorHandleType) payload.errorHandleType = formData.errorHandleType;
      if (formData.gridViewName) payload.gridViewName = formData.gridViewName;

      // Add createdBy and updatedBy
      const currentUser = localStorage.getItem('userEmail');
      if (isCreating) {
        payload.createdBy = currentUser;
      }
      if (formData.updatedBy) {
        payload.updatedBy = formData.updatedBy;
      } else {
        payload.updatedBy = currentUser;
      }

      const url = isCreating
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/${selectedStep.workflowStepId}`;
      const method = isCreating ? 'POST' : 'PUT';

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      const resp = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        let errorText = await resp.text();
        let duplicateMsg = '';
        try {
          const errJson = JSON.parse(errorText);
          if (errJson && errJson.message && errJson.message.toLowerCase().includes('duplicate')) {
            duplicateMsg = 'Duplicate step name or order';
          } else if (errJson && errJson.message && (errJson.message.toLowerCase().includes('step order') || errJson.message.toLowerCase().includes('step name'))) {
            duplicateMsg = 'Duplicate step name or order';
          }
        } catch (e) {
          if (errorText.toLowerCase().includes('duplicate') || errorText.toLowerCase().includes('step order') || errorText.toLowerCase().includes('step name')) {
            duplicateMsg = 'Duplicate step name or order';
          }
        }
        if (duplicateMsg) {
          showError(duplicateMsg, 'Duplicate Error');
          setFormLoading(false);
          return;
        }
        throw new Error(`Failed to ${isCreating ? 'create' : 'update'} workflow step: ${resp.status} ${resp.statusText} - ${errorText}`);
      }

      setFormOpen(false);
      fetchWorkflowSteps();
      showSuccess(`Workflow step ${isCreating ? 'created' : 'updated'} successfully`, 'Success');
    } catch (err) {
      showError(`Failed to ${isCreating ? 'create' : 'update'} workflow step: ${err.message}`, 'Submission Error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedStep(null);
    setIsCreating(false);
    setError(null);
    setFormData({
      workflowDefinitionId: '',
      stepName: '',
      stepOrder: '',
      stepType: '',
      stepSubType: '',
      errorHandleType: '',
      userEditable: false,
      snowflakeViews: '',
      gridViewName: '',
      visibleToCustomer: false,
      snowflakeDestinationTable: '',
      runCondition: '',
      onExit: '',
      onValidation: '',
      validationErrorStep: '',
      onError: '',
      createCheckerScreenTab: '',
      transformDescription: '',
      userActionsTaken: '',
      additionalInstructions: '',
      updatedBy: '',
      isDeleted: false
    });
    setCompareInfo(null); // Reset compareInfo when closing the form
    setConfigData(null);
    setEditedJson('');
    setConfigError(null);
    setEditingConfig(false);
    setViewConfigOpen(false);
  };

  const handleCreateStep = (e) => {
    e.stopPropagation();
    setIsCreating(true);
    setSelectedStep(null);

    // Extract workflowDefinitionId from the first step if available
    const workflowDefinitionId = steps.length > 0 ? steps[0].workflowDefinitionId : '';

    setCompareInfo(null); // Reset compareInfo when creating a new step
    setConfigData(null);
    setEditedJson('');
    setConfigError(null);
    setEditingConfig(false);
    setViewConfigOpen(false);

    setFormData({
      workflowDefinitionId: workflowDefinitionId || '',
      stepName: '',
      stepOrder: '',
      stepType: '',
      stepSubType: '',
      errorHandleType: '',
      userEditable: false,
      snowflakeViews: '',
      gridViewName: '',
      visibleToCustomer: false,
      snowflakeDestinationTable: '',
      runCondition: '',
      onExit: '',
      onValidation: '',
      validationErrorStep: '',
      onError: '',
      createCheckerScreenTab: '',
      transformDescription: '',
      userActionsTaken: '',
      additionalInstructions: '',
      updatedBy: '',
      isDeleted: false
    });
    setFormOpen(true);
  };

  // useEffect(() => {
  //   if (open && workflowId) {
  //     fetchWorkflowSteps();
  //   }
  // }, [open, workflowId]);
  useEffect(() => {
    if (open && workflowDefinitionId) {
      fetchWorkflowSteps();
    }
  }, [open, workflowDefinitionId]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isFullScreen ? '100%' : 600,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0, mb: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            Tenant Workflow Steps
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <IconButton onClick={handleCreateStep} size="small" sx={harmonateDialogCloseIconSx}>
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setIsFullScreen(f => !f)} size="small" sx={harmonateDialogCloseIconSx}>
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 2, height: 400, width: '100%', flex: 1, minHeight: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: ht.blue }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
          ) : (
            <DataGridPro
              columnResizeMode="onEnd"
              resizable
              rows={steps.map((step, idx) => ({
                id: step.workflowStepId || idx + 1,
                stepName: step.stepName || '',
                stepOrder: step.stepOrder || 0,
                stepType: step.stepType || '',
                stepSubType: step.stepSubType || '',
                errorHandleType: step.errorHandleType || '',
                userEditable: step.userEditable ? 'Yes' : 'No',
                visibleToCustomer: step.visibleToCustomer ? 'Yes' : 'No',
                snowflakeViews: step.snowflakeViews || '',
                snowflakeDestinationTable: step.snowflakeDestinationTable || '',
                gridViewName: step.gridViewName || '',
                runCondition: step.runCondition || '',
                onExit: step.onExit || '',
                onValidation: step.onValidation || '',
                validationErrorStep: step.validationErrorStep || '',
                onError: step.onError || '',
                createCheckerScreenTab: step.createCheckerScreenTab || '',
                transformDescription: step.transformDescription || '',
                userActionsTaken: step.userActionsTaken || '',
                additionalInstructions: step.additionalInstructions ?
                  (typeof step.additionalInstructions === 'object' ?
                    JSON.stringify(step.additionalInstructions) :
                    step.additionalInstructions) : '',
                updatedBy: step.updatedBy || '',
                isDeleted: step.isDeleted ? 'Yes' : 'No',
                raw: step
              }))}
              columns={columns}
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              rowHeight={35}
              sx={{ ...harmonateDataGridSx, border: 'none', height: '100%' }}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              onRowClick={(params) => {
                setIsCreating(false);
                fetchStepDetails(params.row.raw.workflowStepId);
              }}
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <Typography variant="body1" sx={{ color: ht.text, fontSize: '0.875rem' }}>
                      No workflow steps found for this workflow
                    </Typography>
                  </Box>
                )
              }}
            />
          )}
        </Box>
      </Drawer>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          {isCreating ? 'Add Workflow Step' : `Edit Workflow Step: ${selectedStep?.stepName}`}
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {formLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>

                  <TextField
                    label="Step Order"
                    name="stepOrder"
                    value={formData.stepOrder}
                    onChange={handleFormChange}
                    fullWidth
                    type="number"
                    required
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                  <TextField
                    label="Step Name"
                    name="stepName"
                    value={formData.stepName}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    select
                    label="Step Type"
                    name="stepType"
                    value={formData.stepType}
                    onChange={handleFormChange}
                    fullWidth
                    required
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                    sx={harmonateTextFieldSx}
                  >
                    {stepTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="User Actions Taken"
                    name="userActionsTaken"
                    value={formData.userActionsTaken}
                    onChange={handleFormChange}
                    fullWidth
                    multiline
                    rows={1}
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                  {/* <FormControl fullWidth>
                    <InputLabel sx={{ fontFamily: 'Roboto, sans-serif' }}>Error Handle Type</InputLabel>
                    <Select
                      name="errorHandleType"
                      value={formData.errorHandleType}
                      onChange={handleFormChange}
                      label="Error Handle Type"
                      sx={{ fontFamily: 'Roboto, sans-serif' }}
                    >
                      {errorHandleTypeOptions.map((option) => (
                        <MenuItem key={option} value={option} sx={{ fontFamily: 'Roboto, sans-serif' }}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* <TextField
                    label="Grid View Name"
                    name="gridViewName"
                    value={formData.gridViewName}
                    onChange={handleFormChange}
                    fullWidth
                    sx={{ fontFamily: 'Roboto, sans-serif' }}
                  /> */}
                  {/* <TextField
                    label="Updated By"
                    name="updatedBy"
                    value={formData.updatedBy}
                    onChange={handleFormChange}
                    fullWidth
                    sx={{ fontFamily: 'Roboto, sans-serif' }}
                  /> */}
                  <TextField
                    label="Run Condition"
                    name="runCondition"
                    value={formData.runCondition}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="On Exit"
                    name="onExit"
                    value={formData.onExit}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                  <TextField
                    label="On Validation"
                    name="onValidation"
                    value={formData.onValidation}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                </Box>

                <TextField
                  label="Validation Error Step"
                  name="validationErrorStep"
                  value={formData.validationErrorStep}
                  onChange={handleFormChange}
                  fullWidth
                  size="small"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="On Error"
                    name="onError"
                    value={formData.onError}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                  <TextField
                    label="Create Checker Screen Tab"
                    name="createCheckerScreenTab"
                    value={formData.createCheckerScreenTab}
                    onChange={handleFormChange}
                    fullWidth
                    size="small"
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                </Box>

                {/* Full width multiline fields */}
                <TextField
                  label="Transform Description"
                  name="transformDescription"
                  value={formData.transformDescription}
                  onChange={handleFormChange}
                  fullWidth
                  multiline
                  rows={1}
                  size="small"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />
                <TextField
                  label="Snowflake Views"
                  name="snowflakeViews"
                  value={formData.snowflakeViews}
                  onChange={handleFormChange}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />

                {/* Compare Views Information - only show for COMPARE type steps */}
                {formData.stepType === 'COMPARE' && (
                  <Box sx={{
                    p: 2,
                    bgcolor: ht.listBg,
                    border: `1px solid ${ht.line}`,
                    borderRadius: ht.cardRadius,
                    mt: 1
                  }}>
                    <Typography variant="subtitle2" sx={{ color: ht.text, mb: 1, fontWeight: 700 }}>
                      Compare Views Configuration
                    </Typography>
                    {configLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ color: ht.blue, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: ht.text, fontSize: '0.875rem' }}>
                          Loading compare views...
                        </Typography>
                      </Box>
                    ) : compareInfo ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text, fontSize: '0.875rem' }}>
                            Source View:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', ml: 1, color: ht.text }}>
                            {compareInfo.sourceView}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text, fontSize: '0.875rem' }}>
                            Compare View:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', ml: 1, color: ht.text }}>
                            {compareInfo.compareView}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: ht.text, fontSize: '0.875rem' }}>
                          No compare configuration available. Click "View Config" to load.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <TextField
                  label="Snowflake Destination Table"
                  name="snowflakeDestinationTable"
                  value={formData.snowflakeDestinationTable}
                  onChange={handleFormChange}
                  fullWidth
                  size="small"
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />
                {/* <TextField
                  label="Additional Instructions (JSON)"
                  name="additionalInstructions"
                  value={formData.additionalInstructions}
                  onChange={handleFormChange}
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Must be valid JSON format"
                  error={formData.additionalInstructions && !isValidJSON(formData.additionalInstructions)}
                  sx={{ fontFamily: 'Roboto, sans-serif' }}
                /> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="Additional Instructions (JSON)"
                    name="additionalInstructions"
                    value={formData.additionalInstructions}
                    onChange={handleFormChange}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    helperText="Must be a valid JSON format.Must be provided if you have a view for the step."
                    error={formData.additionalInstructions && !isValidJSON(formData.additionalInstructions)}
                    InputLabelProps={harmonateInputLabelProps}
                    sx={harmonateTextFieldSx}
                  />
                  <Tooltip title="View JSON Template">
                    <IconButton onClick={() => setJsonTemplateOpen(true)} sx={{ color: ht.text }}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* Switches in a row */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.userEditable}
                        onChange={handleFormChange}
                        name="userEditable"
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="User Editable"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: ht.text } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.visibleToCustomer}
                        onChange={handleFormChange}
                        name="visibleToCustomer"
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="Visible to Customer"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: ht.text } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isDeleted}
                        onChange={handleFormChange}
                        name="isDeleted"
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="Is Deleted"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: ht.text } }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
              <Button
                onClick={async () => {
                  if (formData.additionalInstructions) {
                    // Always refetch: configData may belong to a previously opened step
                    await fetchConfig();
                    setViewConfigOpen(true);
                  } else {
                    setCreatingConfig(true);
                  }
                }}
                disabled={formLoading}
                sx={harmonateAccentLinkSx}
                startIcon={<VisibilityIcon />}
              >
                {formData.additionalInstructions ? 'View Config' : 'Create Config'}
              </Button>
              {formData.additionalInstructions && (
                <Button
                  onClick={() => setCreatingConfig(true)}
                  disabled={formLoading || !canAdd}
                  sx={harmonateAccentLinkSx}
                  startIcon={<AddIcon />}
                >
                  New Config
                </Button>
              )}
            </Box>
            <Button onClick={handleCloseForm} disabled={formLoading} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
              sx={harmonateProceedButtonSx}
            >
              {formLoading ? <CircularProgress size={24} sx={{ color: ht.white }} /> : isCreating ? 'Save' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* Create Config Dialog */}
      <Dialog open={creatingConfig} onClose={() => setCreatingConfig(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          Create New Configuration
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="View Category Name"
              value={newConfig.viewDCategory}
              onChange={(e) => setNewConfig(prev => ({ ...prev, viewDCategory: e.target.value }))}
              fullWidth
              required
              size="small"
              placeholder="Enter a unique category name"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            {/* <TextField
              label="JSON Configuration"
              value={newConfig.viewJsonDefault}
              onChange={(e) => setNewConfig(prev => ({ ...prev, viewJsonDefault: e.target.value }))}
              fullWidth
              multiline
              rows={10}
              required
              placeholder="Enter your JSON configuration"
              helperText="Enter a valid JSON object"
              error={newConfig.viewJsonDefault && !isValidJSON(newConfig.viewJsonDefault)}
              sx={{ fontFamily: 'Roboto, sans-serif' }}
            /> */}
            <TextField
              label="JSON Configuration"
              value={newConfig.viewJsonDefault}
              onChange={(e) => {
                setNewConfig(prev => ({
                  ...prev,
                  viewJsonDefault: e.target.value
                }));
              }}
              onBlur={(e) => {
                // Format the JSON when user leaves the field
                const formatted = formatJsonString(e.target.value);
                if (formatted !== e.target.value) {
                  setNewConfig(prev => ({
                    ...prev,
                    viewJsonDefault: formatted
                  }));
                }
              }}
              fullWidth
              multiline
              rows={10}
              required
              size="small"
              placeholder='Example: {"key": "value"}'
              helperText={newConfig.viewJsonDefault && !isValidJSON(newConfig.viewJsonDefault)
                ? "Invalid JSON format"
                : "Enter a valid JSON object"}
              error={newConfig.viewJsonDefault ? !isValidJSON(newConfig.viewJsonDefault) : false}
              InputLabelProps={harmonateInputLabelProps}
              sx={{
                ...harmonateTextFieldSx,
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }
              }}
            />
            {configError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {configError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setCreatingConfig(false)}
            disabled={configSaving}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateConfig}
            variant="contained"
            disabled={!newConfig.viewDCategory || !newConfig.viewJsonDefault || configSaving || !isValidJSON(newConfig.viewJsonDefault)}
            startIcon={configSaving ? <CircularProgress size={20} sx={{ color: ht.white }} /> : <SaveIcon />}
            sx={harmonateProceedButtonSx}
          >
            {configSaving ? 'Creating...' : 'Create Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Edit Config Dialog */}
      <Dialog open={viewConfigOpen} onClose={() => setViewConfigOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          {editingConfig ? 'Edit Configuration' : 'View Configuration'}
          <IconButton
            aria-label={editingConfig ? 'cancel edit' : 'edit json'}
            onClick={() => setEditingConfig(!editingConfig)}
            sx={harmonateDialogCloseIconSx}
            disabled={configLoading || !configData}
          >
            {editingConfig ? <CloseIcon /> : <EditIcon />}
          </IconButton>
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body2" sx={{ color: ht.text, fontSize: '0.875rem', mb: 2 }}>
            {editingConfig ? 'Edit the configuration JSON below:' : 'Configuration from API based on additionalInstructions view_name:'}
          </Typography>
          {configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
              <CircularProgress size={24} sx={{ color: ht.blue }} />
            </Box>
          ) : configError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {configError}
            </Alert>
          ) : configData ? (
            <Box sx={{
              p: 2,
              bgcolor: editingConfig ? ht.white : ht.listBg,
              border: editingConfig ? `1px solid ${ht.line}` : 'none',
              borderRadius: ht.cardRadius,
              maxHeight: '60vh',
              overflow: 'auto',
              '& textarea': {
                width: '100%',
                minHeight: '300px',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                border: 'none',
                outline: 'none',
                resize: 'vertical',
                padding: '8px',
                lineHeight: 1.5,
                color: ht.text,
              },
              '& pre': {
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: ht.text,
              }
            }}>
              {editingConfig ? (
                <textarea
                  value={editedJson}
                  onChange={(e) => setEditedJson(e.target.value)}
                  disabled={configSaving}
                />
              ) : (
                <pre>{JSON.stringify(configData, null, 2)}</pre>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No config data available. Check additionalInstructions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {editingConfig ? (
            <>
              <Button
                onClick={() => {
                  setEditingConfig(false);
                  setEditedJson(JSON.stringify(configData, null, 2));
                }}
                disabled={configSaving}
                variant="outlined"
                sx={harmonateCancelButtonSx}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveConfig}
                variant="contained"
                disabled={configSaving}
                startIcon={configSaving ? <CircularProgress size={20} sx={{ color: ht.white }} /> : <SaveIcon />}
                sx={harmonateProceedButtonSx}
              >
                {configSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={async () => {
                  const textToCopy = configData ? JSON.stringify(configData, null, 2) : JSON.stringify(formData, null, 2);
                  try {
                    // Modern async clipboard API
                    if (navigator.clipboard) {
                      await navigator.clipboard.writeText(textToCopy);
                      showSuccess('JSON copied to clipboard!', 'Copied');
                    } else {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = textToCopy;
                      textArea.style.position = 'fixed';  // Avoid scrolling to bottom
                      document.body.appendChild(textArea);
                      textArea.focus();
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        showSuccess('JSON copied to clipboard!', 'Copied');
                      } catch (err) {
                        console.error('Fallback copy failed:', err);
                        showError('Failed to copy text. Please try again or copy manually.');
                      }
                      document.body.removeChild(textArea);
                    }
                  } catch (err) {
                    console.error('Could not copy text:', err);
                    showError('Failed to copy text. Please try again or copy manually.');
                  }
                }}
                startIcon={<FileCopyIcon />}
                sx={harmonateAccentLinkSx}
              >
                Copy to Clipboard
              </Button>
              <Button
                onClick={() => setViewConfigOpen(false)}
                variant="outlined"
                sx={harmonateCancelButtonSx}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={jsonTemplateOpen} onClose={() => setJsonTemplateOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          Additional Instructions JSON Template
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body2" sx={{ color: ht.text, fontSize: '0.875rem', mb: 2 }}>
            Use one of the following JSON formats for the Additional Instructions field:
          </Typography>
          <Box sx={{ bgcolor: ht.listBg, p: 2, borderRadius: ht.cardRadius, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: ht.text, fontWeight: 700, mb: 1 }}>
              Format 1: DDL and Grid View
            </Typography>
            <pre style={{ fontFamily: 'monospace', fontSize: '14px', whiteSpace: 'pre-wrap', color: ht.text }}>
              {JSON.stringify(
                {
                  ddl: { view_name: "SCHEMA.SNOWFLAKE_VIEW_NAME" },
                  grid: { view_name: "SCHEMA.SNOWFLAKE_VIEW_NAME" }
                },
                null,
                2
              )}
            </pre>
          </Box>
          <Box sx={{ bgcolor: ht.listBg, p: 2, borderRadius: ht.cardRadius }}>
            <Typography variant="subtitle2" sx={{ color: ht.text, fontWeight: 700, mb: 1 }}>
              Format 2: Grid View Only
            </Typography>
            <pre style={{ fontFamily: 'monospace', fontSize: '14px', whiteSpace: 'pre-wrap', color: ht.text }}>
              {JSON.stringify(
                {
                  grid: { view_name: "SCHEMA.SNOWFLAKE_VIEW_NAME" }
                },
                null,
                2
              )}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setJsonTemplateOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notification */}
      {toastState.open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            maxWidth: 400
          }}
        >
          <Alert
            severity={toastState.severity}
            onClose={hideToast}
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">{toastState.title}</Typography>
            <Typography variant="body2">{toastState.message}</Typography>
          </Alert>
        </Box>
      )}
    </>
  );
}

export default TenantWorkflowStepsDrawer;